import {
  AccountInfo,
  InteractionRequiredAuthError,
  IPublicClientApplication,
  SilentRequest,
} from '@azure/msal-browser';
import { loginRequest } from './authConfig.ts';

/**
 * Acquires a token silently, with automatic refresh if the token is expired
 * @param msalInstance - The MSAL instance
 * @param account - The user account
 * @returns A promise that resolves to the ID token
 */
export const getTokenWithRefresh = async (
  msalInstance: IPublicClientApplication,
  account: AccountInfo,
): Promise<string> => {
  const request: SilentRequest = {
    ...loginRequest,
    account: account,
  };

  try {
    // Try to get the token silently - this will succeed if the token is still valid
    const response = await msalInstance.acquireTokenSilent(request);
    return response.idToken;
  } catch (error) {
    // If the silent token acquisition fails due to token expiration or other issues
    if (error instanceof InteractionRequiredAuthError) {
      // Fall back to an interactive method if silent refresh fails
      console.log(
        'Silent token acquisition failed, acquiring token using redirect',
      );
      try {
        const response = await msalInstance.acquireTokenPopup(request);
        return response.idToken;
      } catch (popupError) {
        console.error('Error during popup token acquisition', popupError);
        throw popupError;
      }
    } else {
      console.error('Error during silent token acquisition', error);
      throw error;
    }
  }
};

/**
 * Gets the current active account from MSAL
 * @param msalInstance - The MSAL instance
 * @returns The active account or null if no account is active
 */
export const getActiveAccount = (
  msalInstance: IPublicClientApplication,
): AccountInfo | null => {
  // First, check if there's an active account
  const activeAccount = msalInstance.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  // If no active account, get all accounts and use the first one if available
  const accounts = msalInstance.getAllAccounts();
  if (accounts.length > 0) {
    // Set the first account as active
    msalInstance.setActiveAccount(accounts[0]);
    return accounts[0];
  }

  return null;
};

/**
 * Gets a token for the current user with automatic refresh if needed
 * @param msalContext - The MSAL context from useMsal hook
 * @returns A promise that resolves to the access token or null if no account is available
 */
export const getAuthToken = async (
  msalContext: { instance: IPublicClientApplication } | null | undefined,
): Promise<string | null> => {
  if (!msalContext) {
    console.error('MSAL context is not available');
    return null;
  }

  const msalInstance = msalContext.instance;
  const account = getActiveAccount(msalInstance);

  if (!account) {
    console.error('No active account found');
    return null;
  }

  try {
    return await getTokenWithRefresh(msalInstance, account);
  } catch (error) {
    console.error('Failed to acquire token', error);
    return null;
  }
};
